/**
 * Lease state → panel state.
 *
 * The last wire: the control plane owns lease lifecycle, the host emits events,
 * and the panel renders a state. This maps one to the other.
 *
 * Pure on purpose. This is the function that decides whether a user is told
 * "live" or "blocked", and getting it wrong shows a working simulator that is
 * not there — or hides one that is. That decision should be assertable without
 * a running lease, a socket, or a browser.
 *
 * The mapping is NOT one-to-one, and the places it diverges are the point:
 *
 *   - a lease is `active` from the control plane's view the moment it is
 *     claimed, but the panel must not say `live` until frames are arriving;
 *   - `blocked` is terminal on both sides, so it maps straight through;
 *   - readiness comes from the host's capabilities, not the lease — a host with
 *     no iOS runtime cannot serve any lease, and saying "queued" would be a lie.
 */

import type { SimulatorPanelState } from "./simulatorPanelState";

/** The control plane's lease states (CMMD `forge_sim_leases.state`). */
export type LeaseState = "allocating" | "active" | "releasing" | "released" | "blocked";

/** Host events the panel cares about, from the bridge protocol. */
export type HostSignal = "device.booted" | "device.erased" | "host.draining" | "error" | null;

export interface HostReadiness {
  /** False when the host reports no iOS runtime — it cannot serve any lease. */
  hasIosRuntime: boolean;
  /** False while the host has not reported capabilities yet. */
  reported: boolean;
}

export interface LeaseSnapshotForPanel {
  leaseState: LeaseState | null;
  readiness: HostReadiness;
  lastSignal: HostSignal;
  /** Whether the user has agreed to allocate on this project. */
  consentGiven: boolean;
  /** Set when the allocator has the request but no host is free. */
  queuedBehind: number;
  /** Frames only start once the stream is up; null means none yet. */
  lastFrameAtMs: number | null;
  nowMs: number;
  /** Non-null while the host is retrying after a crash. */
  retrySeconds: number | null;
}

/**
 * Resolve what the panel should show.
 *
 * Ordered by precedence rather than by lease-state switch, because several of
 * these outrank the lease: a blocked lease is terminal no matter what the host
 * last said, and a host with no runtime cannot be "allocating" however hopeful
 * the control plane is.
 */
const FRAME_STALENESS_MS = 2_000;

/**
 * Rules that outrank the lease entirely, in precedence order.
 *
 * Each returns a state to stop at, or null to defer. Split out because a single
 * function carrying both the precedence chain and the lease mapping reached
 * cyclomatic 16 — and because "what outranks a lease" is a different question
 * from "what does this lease look like", so they read better apart.
 */
const OVERRIDES: ReadonlyArray<(input: LeaseSnapshotForPanel) => SimulatorPanelState | null> = [
  // Terminal first. Nothing below can rescue a blocked lease, and checking it
  // later would let a stale host signal present it as retryable.
  (input) => (input.leaseState === "blocked" ? "blocked" : null),
  // Readiness outranks the lease. A host with no iOS runtime cannot serve any
  // lease, so reporting "queued" would promise something that cannot arrive.
  (input) => (!input.readiness.reported ? "checking-setup" : null),
  (input) => (!input.readiness.hasIosRuntime ? "setup-incomplete" : null),
  // Crash-retry is a host condition, not a lease state: the lease is still held
  // while the device restarts.
  (input) => (input.retrySeconds !== null ? "crash-retrying" : null),
  (input) => (!input.consentGiven ? "awaiting-consent" : null),
];

/**
 * What an `active` lease actually looks like to a viewer.
 *
 * The divergence that matters: the control plane calls a lease active the moment
 * it is claimed, but the panel must not claim `live` until frames are arriving,
 * or a wedged simulator renders as a working one.
 */
function activeLeaseState(input: LeaseSnapshotForPanel): SimulatorPanelState {
  if (input.lastFrameAtMs === null) return "booting";
  const fresh = input.nowMs - input.lastFrameAtMs <= FRAME_STALENESS_MS;
  // Frames arrived before but stopped: reconnecting, not booting.
  return fresh ? "live" : "reconnecting";
}

function leaseOnlyState(input: LeaseSnapshotForPanel): SimulatorPanelState {
  switch (input.leaseState) {
    case null:
      return input.queuedBehind > 0 ? "queued" : "allocating";
    case "allocating":
      // `device.booted` means the simulator is up but the stream may not be.
      return input.lastSignal === "device.booted" ? "booting" : "allocating";
    case "active":
      return activeLeaseState(input);
    case "releasing":
    case "released":
      // Not user-facing: return to a state a new session can start from rather
      // than showing a dead device.
      return "awaiting-consent";
    default:
      return "allocating";
  }
}

export function leaseToPanelState(input: LeaseSnapshotForPanel): SimulatorPanelState {
  for (const override of OVERRIDES) {
    const state = override(input);
    if (state !== null) return state;
  }
  return leaseOnlyState(input);
}

/**
 * Whether the panel should keep polling.
 *
 * Terminal states stop, so a blocked lease does not spin a request loop
 * forever against a host that will never answer differently.
 */
export function shouldKeepPolling(state: SimulatorPanelState): boolean {
  return state !== "blocked" && state !== "setup-incomplete";
}
